import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    useListContext,
    useRefresh,
    useNotify,
    useUnselectAll,
    useDataProvider,
    Confirm,
} from 'react-admin';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SecurityIcon from '@mui/icons-material/Security';

const ADMIN_APP_GUID = '13b312b5-367c-4c74-bb77-bdb34df4e721';

const BulkChangeRoleButton = () => {
    const { selectedIds } = useListContext();
    const refresh = useRefresh();
    const notify = useNotify();
    const unselectAll = useUnselectAll('user');
    const dataProvider = useDataProvider();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRole, setSelectedRole] = useState<{
        id: number;
        label: string;
    } | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [roleOptions, setRoleOptions] = useState<
        { id: number; item_title: string }[]
    >([]);

    useEffect(() => {
        dataProvider
            .getAny('extralist/userapplication/userrights', '')
            .then(({ data }) => setRoleOptions(data))
            .catch(() => {});
    }, [dataProvider]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleRoleSelect = (role: { id: number; item_title: string }) => {
        setSelectedRole({ id: role.id, label: role.item_title });
        setAnchorEl(null);
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        if (!selectedRole) return;
        setLoading(true);
        setConfirmOpen(false);

        try {
            let successCount = 0;
            let errorCount = 0;

            for (const userId of selectedIds) {
                try {
                    const { data: uaList } = await dataProvider.getList(
                        'userapplication',
                        {
                            filter: { user_guid: userId },
                            pagination: { page: 1, perPage: 100 },
                            sort: { field: 'id', order: 'ASC' },
                        }
                    );

                    const ua = uaList.find(
                        (item: any) => item.application_guid === ADMIN_APP_GUID
                    );

                    if (ua) {
                        await dataProvider.update('userapplication', {
                            id: ua.guid,
                            data: {
                                ...ua,
                                public: {
                                    ...ua.public,
                                    userrights: selectedRole.id,
                                },
                            },
                            previousData: ua,
                        });
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch {
                    errorCount++;
                }
            }

            if (successCount > 0) {
                notify(
                    `${successCount} käyttäjän rooli vaihdettu: ${selectedRole.label}`,
                    { type: 'success' }
                );
            }
            if (errorCount > 0) {
                notify(`${errorCount} käyttäjän roolin vaihto epäonnistui`, {
                    type: 'warning',
                });
            }
            refresh();
            unselectAll();
        } catch {
            notify('Virhe roolia vaihdettaessa', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                disabled={loading || roleOptions.length === 0}
                color="primary"
                size="small"
            >
                <SecurityIcon sx={{ mr: 0.5 }} fontSize="small" />
                Vaihda rooli
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {roleOptions.map(role => (
                    <MenuItem
                        key={role.id}
                        onClick={() => handleRoleSelect(role)}
                    >
                        {role.item_title}
                    </MenuItem>
                ))}
            </Menu>
            <Confirm
                isOpen={confirmOpen}
                loading={loading}
                title="Vaihda käyttäjien rooli"
                content={`Haluatko varmasti vaihtaa ${selectedIds.length} valitun käyttäjän rooliksi "${selectedRole?.label}"?`}
                onConfirm={handleConfirm}
                onClose={() => setConfirmOpen(false)}
            />
        </>
    );
};

export default BulkChangeRoleButton;
